// frontend/src/components/Common/AIAnalysisModal.js
import React, { useState, useEffect } from 'react';
import { 
    XMarkIcon, 
    SparklesIcon,
    CheckCircleIcon,
    ClipboardIcon,
    ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/outline';

const AIAnalysisModal = ({ isOpen, onClose, article, onAnalyze }) => {
    const [isCopied, setIsCopied] = useState(false);
    const [copyError, setCopyError] = useState('');

    useEffect(() => {
        if (isOpen) {
            // Reset state when modal opens
            setIsCopied(false);
            setCopyError('');
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleCopyToClipboard = async () => {
        try {
            await onAnalyze(article, false); // Copy without opening
            setIsCopied(true);
            setCopyError('');
        } catch (error) {
            console.error('Failed to copy content:', error);
            setCopyError('Failed to copy content to clipboard');
        }
    };

    const handleOpenGemini = async () => {
        try {
            await onAnalyze(article, true); // Copy and open
            onClose(); // Close modal after opening Gemini
        } catch (error) {
            console.error('Failed to open Gemini:', error);
            setCopyError('Failed to process request');
        }
    };

    const handleClose = () => {
        setIsCopied(false);
        setCopyError('');
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50 p-4 transition-opacity duration-300 ease-in-out">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full transform transition-all duration-300 ease-in-out">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <div className="flex items-center">
                        <SparklesIcon className="h-6 w-6 text-purple-600 mr-3" />
                        <h3 className="text-lg font-semibold text-gray-900">
                            AI Analysis
                        </h3>
                    </div>
                    <button
                        onClick={handleClose}
                        className="text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 rounded-full p-1"
                    >
                        <XMarkIcon className="h-6 w-6" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    <div className="mb-4">
                        <h4 className="text-sm font-medium text-gray-900 mb-2">
                            Article: {article?.title}
                        </h4>
                        <p className="text-sm text-gray-600">
                            Choose how you'd like to analyze this article with AI:
                        </p>
                    </div>

                    {/* Status Messages */}
                    {isCopied && (
                        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
                            <div className="flex items-center">
                                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                                <span className="text-sm text-green-700 font-medium">
                                    Content copied to clipboard!
                                </span>
                            </div>
                            <p className="text-xs text-green-600 mt-1">
                                You can now paste it in any AI service.
                            </p>
                        </div>
                    )}

                    {copyError && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                            <p className="text-sm text-red-700">{copyError}</p>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="space-y-3">
                        {/* Copy to Clipboard Button */}
                        <button
                            onClick={handleCopyToClipboard}
                            className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-colors"
                        >
                            <ClipboardIcon className="h-4 w-4 mr-2" />
                            Copy to Clipboard
                        </button>

                        {/* Open Gemini Button */}
                        <button
                            onClick={handleOpenGemini}
                            className="w-full flex items-center justify-center px-4 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white text-sm font-medium rounded-md hover:from-purple-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-all duration-200"
                        >
                            <SparklesIcon className="h-4 w-4 mr-2" />
                            Copy, Open Gemini (Paste yourself)
                            <ArrowTopRightOnSquareIcon className="h-4 w-4 ml-2" />
                        </button>

                        {/* Future AI Services Placeholder */}
                        <div className="pt-2 border-t border-gray-200">
                            <p className="text-xs text-gray-500 text-center mb-2">
                                More AI services coming soon:
                            </p>
                            <div className="flex justify-center space-x-2">
                                <button
                                    disabled
                                    className="px-3 py-1.5 text-xs text-gray-400 bg-gray-100 rounded-md cursor-not-allowed"
                                >
                                    ChatGPT (Soon)
                                </button>
                                <button
                                    disabled
                                    className="px-3 py-1.5 text-xs text-gray-400 bg-gray-100 rounded-md cursor-not-allowed"
                                >
                                    Claude (Soon)
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 rounded-b-lg">
                    <button
                        onClick={handleClose}
                        className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-colors"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AIAnalysisModal;
